import { ReactElement, ReactNode } from "react";
import { LabelColor } from "src/components/Label";
export type nNumber = number|null
export type nDate = Date|null
export type nString = string|null

export type ActiveAccount = {
    Account: string    
}

export type Portfolio = {
    id: number
    DeskID: number
    Name: string
    Description: string
    TakeInGlobalPosition: boolean
    TakeInDeskPosition: boolean
    VaRLimit: nNumber
    VolumeLimit: nNumber
    NominalLimit: nNumber
    DrawdownLimit: nNumber
    StopLossLimit: nNumber
    GammaLimit: nNumber
    VegaLimit: nNumber
    YtdPnLLimit: nNumber
    Accounts: PortfolioAccount[]
}

export type PortfolioAccount = {
    id: number
    PortfolioID: number
    Account: string
}

export type LimitStatus = {
    Tittle: string
    Limit: nNumber
    Usage: nNumber
    Value: nNumber
    AlertColor: LabelColor
    HasAlert: boolean
}

export type PortfolioRiskStatus = {
    PortfolioId: number
    Portfolio: string
    LimtitStatus: LimitStatus[]
}

export type DeskStatus = {
    DeskID: number
    Desk: string
    NPortfolios: number
    NAccounts: number
    IsValid: boolean
    Error: string
}

export const SendDeskPositionReportDepthLevelNames = [
    { id: 1, name: 'Level 1', Description: 'In Desk Position expand to Commodity Level' }, { id: 2, name: 'Level 2', Description: 'In Desk Position expand to Full Product Level' }, { id: 3, name: 'Level 3', Description: 'All levels expanded' }] as const;
export const SendDeskPositionReportDepthLevelTypes = SendDeskPositionReportDepthLevelNames.map(o => o.id);
export type SendDeskPositionReportDepthLevelType = typeof SendDeskPositionReportDepthLevelTypes[number];
export const DefaultSendDeskPositionReportDepthLevelType: SendDeskPositionReportDepthLevelType = 3;

export const ReportReceiverModes = ['to','cc','bcc'] as const
export type ReportReceiverMode = typeof ReportReceiverModes[number];

export type ReportReceiver = {
    id: number
    email: string
    mode: ReportReceiverMode
}

export type RiskDesk = {
    id: number
    name: string
    GroupDeskID: number
    ExternalCode1: string
    Active: boolean
    SendInAllDeskPositionReport?: boolean
    SendDeskPositionReportDepthLevel: SendDeskPositionReportDepthLevelType
    ReportReceivers: ReportReceiver[]
}

const ALL_LIMIT_KEY = ['VolumeLimit', 'NominalLimit', 'VaRLimit', 'DrawdownLimit', 'GammaLimit', 'VegaLimit', 'YtdPnLLimit', 'StopLossLimit'] as const;
type LimitKeyTuple = typeof ALL_LIMIT_KEY;
export type LimitKey = LimitKeyTuple[number]
export const AllLimitKeys: { key: LimitKey, name: string }[]= [
    { key:'VolumeLimit', name: 'Volume (MT)' }, 
    { key:'NominalLimit', name: 'Nominal (USD)' }, 
    { key:'VaRLimit', name: 'Value in Risk (VaR)' }, 
    { key:'DrawdownLimit', name: 'Drawdown' }, 
    { key:'GammaLimit', name: 'Gamma (Γ)' }, 
    { key:'VegaLimit', name: 'Vega (ν)' }, 
    { key:'YtdPnLLimit', name: 'Ytd PnL' }, 
    { key:'StopLossLimit', name: 'Stop-Loss' }
];

export type PortfolioKeyLimitAlert = {
    id: number
    key: LimitKey
    per: number
    flagColor: LabelColor
    bellNote: nString
}

export type PortfolioKeyLimit = {
    key: LimitKey
    value: nNumber
    alerts: PortfolioKeyLimitAlert[] 
}

export type PortfolioLimit = {
    PortfolioId: number
    Limits: PortfolioKeyLimit[]
}

export type BaseReportSetting = {
    id: number
    name: string
    code: string
    Receivers: ReportReceiver[]
}

export type RiskManagementSettingType = {
    root: string
    desks: string
    portfolios: string
    limitAlerts: string
    reports: string
}

export type SpecialConfigReportSetting = BaseReportSetting & {
    isSpecialConfig: true
    specialConfigAlert: string
    specialConfigURL: keyof RiskManagementSettingType
}

export type CommonReportSetting = BaseReportSetting & {
    isSpecialConfig: false
}

export type ReportSetting = SpecialConfigReportSetting | CommonReportSetting;

export type ReportStatus = {
    IsValid: boolean
    Error: string
}

export type BaseMaturityType = {
    Maturity0: nNumber
    Maturity1: nNumber
    Maturity2: nNumber
    Maturity3: nNumber
    Maturity4: nNumber
    Maturity5: nNumber
    Maturity6: nNumber
    Maturity7: nNumber
    Maturity8: nNumber
    Maturity9: nNumber
    Maturity10: nNumber
    Maturity11: nNumber
    Maturity12: nNumber
    Maturity13: nNumber
    Maturity14: nNumber
    Maturity15: nNumber
    Maturity16: nNumber
    Maturity17: nNumber
    Maturity18: nNumber
    Maturity19: nNumber
    Maturity20: nNumber
    Maturity21: nNumber
    Maturity22: nNumber
    Maturity23: nNumber
    Forward: nNumber
}

export type RiskReportMetricKeys = 'drawdown'|'maxdrawdown'|'VaR'|'VaRLessOne'|'VaRDelta'|'VaRES'|'VaR-limit'|'MtdPnL'|'YtdPnL';
export type RiskReportObjectKeys = 'portfolioid'|'commodityclusterid'|'commodityid'|'deskid';

export type RiskReportMetric = {
    DeskID: number
    ObjectKey: RiskReportObjectKeys
    ObjectID: string
    MetricKey: RiskReportMetricKeys
    MetricValue: nNumber
}

type BaseLiteralUnion<T> = T extends string ? T : never;
export type LiteralUnion = BaseLiteralUnion<string>

export type ReportTab<T extends LiteralUnion> = {
    value: T
    title: string
    icon: ReactElement
    component: ReactNode
    action?: ReactNode
}

export interface IRiskReportData {
    desksStatus: DeskStatus[], 
    reportSetting: ReportSetting, 
    riskDesks: RiskDesk[] 
}

export type BaseRiskReportParams = {
    date: Date,
    extraParams?: { [key: string]: any }
}

export interface ITChangable<T> {
    HasTChanges: boolean,
    TChangedKeys: (keyof T)[]
}

export type ClosePositionType = 1;
export type NoCloseOpenPositionType = 2;
export type OpenPositonType = 99;

export const ClosePositionConst: ClosePositionType = 1;
export const NoCloseOpenPositionConst: NoCloseOpenPositionType = 2;
export const OpenPositonConst: OpenPositonType = 99;

export type RowStatusType = ClosePositionType|NoCloseOpenPositionType|OpenPositonType;

export interface IStatusable {
    Status: RowStatusType
}

export const StatusTypes = new Map<RowStatusType, string>([
    [OpenPositonConst, "Open Positions"], 
    [NoCloseOpenPositionConst, "Open & Only Balances"],
    [ClosePositionConst, "All Positions"],
]);